import React, {FC, useMemo} from 'react';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {MultipleGroupsDropdownBox} from '../../rules/ruleEditor/dropdowns/multipleGroupsDropdownBox';
import {DropdownGroup} from '../../rules/ruleEditor/dropdowns/multipleGroupsDropdownHelpers';
import {RuleTriggersTypesEnum} from '../../rules/ruleTriggerModel';
import {useExpressionsGroups} from './expressionGroupHelpers';
import {ExpressionGroupTitle} from './expressionGroupTitle';
import {getExpressionTypeDropdownIcon} from './expressionIcons';
import {PreExpressionItem} from './expressionModel';
import {ExpressionDependencies, ExpressionStateActions} from './expressionState';

/*
 * Types.
 */

interface ExpressionsPickerProps {
  onAddExpression: ExpressionStateActions['onAddExpression'];
  dependencies: ExpressionDependencies;
  triggers?: ReadonlyArray<RuleTriggersTypesEnum>;
}

/*
 * Component.
 */

export const ExpressionsPicker: FC<ExpressionsPickerProps> = ({
  onAddExpression,
  dependencies,
  triggers,
  children,
}) => {
  const theme = useUserTheme();
  const editorExpressionsGroups = useExpressionsGroups(dependencies, triggers);

  const groups = useMemo(
    () =>
      editorExpressionsGroups.map((group) => {
        const dropdownGroup: DropdownGroup<PreExpressionItem> = {
          title: <ExpressionGroupTitle id={group.id} />,
          items: group.items,
          serializeItemForSearch: (item) => item.name,
          renderItemLabel: (item) => item.name,
          renderItemTooltip: (item) => item.tooltip,
          renderItemIcon: (item) => (
            <Icon name={getExpressionTypeDropdownIcon(item.inferredType)} color={theme.greys.shade70} />
          ),
          getItemKey: (item) => `${group.id}-${item.id}`,
        };
        return dropdownGroup;
      }),
    [editorExpressionsGroups],
  );

  return (
    <MultipleGroupsDropdownBox
      width={350}
      groups={groups}
      selectedItems={[]}
      canSelectMultipleItems={false}
      onChange={(selectedValue: ReadonlyArray<PreExpressionItem>) => onAddExpression(selectedValue[0].atoms)}
      renderButton={() => children}
      hasBackground
    />
  );
};
